<template>
  <div class="account-summary-container">
    <b-row>
      <b-col cols="6" sm="4" md="3" lg="2">
        <action-button
          variant="text"
          extraClass="mb-3 removeVerticalPadding"
          :click="goToDashboard"
        >
          <img
            src="@/assets/images/icons/arrow-left.svg"
            alt="back"
            class="mr-2"
          />
          {{ $t('accountSummary.back') }}
        </action-button>
      </b-col>
    </b-row>
    <page-header :title="$t('accountSummary.heading')" />
    <div class="spacing-24"></div>
    <b-row>
      <b-col xs="12" md="10" lg="8">
        <div class="drip-h3 color-noble-blue-500">
          {{ $t('accountSummary.title') }}
        </div>
        <div class="spacing-8"></div>
        <b-table
          sort-icon-left
          :fields="$t('accountSummary.accountSummaryFields')"
          :items="accounts"
          :no-sort-reset="true"
          class="drip-v2-table table-borderless"
          tbody-tr-class="drip-tr-class"
          thead-class="drip-table-head"
        >
          <template #cell(reference)="data">
            {{ data }}
          </template>
        </b-table>
      </b-col>
    </b-row>
    <div class="spacing-16"></div>
    <div class="drip-h4 font-weight-bold color-noble-blue-500">
      {{ $t('accountSummary.note.title') }}
    </div>
    <div class="spacing-2"></div>
    <ul class="pl-4">
      <li>
        <div class="drip-label font-weight-400 color-noble-blue-500">
          {{ $t('accountSummary.note.list_item_1') }}
        </div>
      </li>
      <li>
        <div class="drip-label font-weight-400 color-noble-blue-500">
          {{ $t('accountSummary.note.list_item_2') }}
        </div>
      </li>
    </ul>

    <div class="spacing-8"></div>

    <form @submit.prevent="downloadExcel" novalidate>
      <!-- Buyer Dropdown -->
      <div class="input-container color-noble-blue-500">
        <div class="drip-body">{{ $t('accountSummary.buyer.title') }}</div>
        <div class="spacing-4"></div>
        <div class="drip-label font-weight-400">
          {{ $t('accountSummary.buyer.label') }}
        </div>
        <div class="spacing-8"></div>
        <div class="input-block width-800">
          <multiselect
            id="buyer"
            name="buyer"
            data-vv-as="Buyer"
            :placeholder="buyerLoading ? 'Loading...' : 'Select a buyer'"
            v-model="buyer"
            :options="buyersData"
            :disabled="buyerLoading"
            v-validate="'required'"
            class="drip-dropdown"
            track-by="importer"
            label="importer"
            selectLabel=""
            selectedLabel=""
            deselectLabel=""
            @open="isOpen = true"
            @close="isOpen = false"
          >
            <template slot="option" slot-scope="{ option }">
              <div class="drip-body font-weight-400 color-noble-blue-500">
                {{ option.importer }}
              </div>
            </template>
            <template slot="caret">
              <img
                v-if="isOpen"
                src="@/assets/images/icons/chevron-down.svg"
                alt="caret"
                class="action-input"
              />
              <img
                v-else
                src="@/assets/images/icons/chevron-down.svg"
                alt="caret"
                class="action-input inverse"
              />
            </template>
          </multiselect>
          <span v-if="errors.has('buyer')" class="error">
            {{ errors.first('buyer') }}
          </span>
        </div>
      </div>

      <div class="spacing-8"></div>

      <!-- Date range checkboxes -->
      <div class="input-container mb-2">
        <div class="drip-body color-noble-blue-500">
          {{ $t('accountSummary.date_range.title') }}
        </div>
        <div class="spacing-8"></div>
        <div class="input-block width-800">
          <b-form-group v-slot="{ ariaDescribedby }" class="checkbox-container">
            <b-form-radio
              v-model="dateRangeSelected"
              :aria-describedby="ariaDescribedby"
              data-vv-as="Date range"
              name="date-range"
              class="w-auto mr-4"
              size="lg"
              value="A"
            >
              <div class="cursor-pointer drip-label font-weight-400">
                {{ $t('accountSummary.date_range.option_1') }}
              </div>
            </b-form-radio>
            <b-form-radio
              v-model="dateRangeSelected"
              :aria-describedby="ariaDescribedby"
              data-vv-as="Date range"
              name="date-range"
              class="w-auto mr-4"
              size="lg"
              value="B"
            >
              <div class="cursor-pointer drip-label flex-wrap font-weight-400">
                {{ $t('accountSummary.date_range.option_2') }}&nbsp;
                <span>{{ getLastThreeMonthLabel() }}</span>
              </div>
            </b-form-radio>
            <b-form-radio
              v-model="dateRangeSelected"
              :aria-describedby="ariaDescribedby"
              data-vv-as="Date range"
              name="date-range"
              class="w-auto mr-4"
              size="lg"
              value="C"
            >
              <div
                class="cursor-pointer drip-label flex-wrap font-weight-400 flex"
              >
                {{ $t('accountSummary.date_range.option_3') }}&nbsp;
                <span>{{ getLastFinancialLabel() }}</span>
              </div>
            </b-form-radio>
          </b-form-group>
        </div>
      </div>

      <div class="divider-container">
        <b-row class="d-flex align-items-center">
          <b-col class="divider ml-3"></b-col>
          <b-col
            cols="false"
            class="px-2 font-weight-bold color-noble-blue-500"
          >
            {{ $t('accountSummary.date.or') }}
          </b-col>
          <b-col class="divider mr-3"></b-col>
        </b-row>
      </div>

      <div class="spacing-8"></div>

      <!-- Date range inputs -->
      <b-row class="divider-container">
        <b-col xs="12" md="6">
          <div class="input-container">
            <div class="drip-label font-weight-400">
              {{ $t('accountSummary.date.from') }}
            </div>
            <div class="spacing-8"></div>
            <div class="input-block">
              <input
                id="startDate"
                name="startDate"
                data-vv-as="From date"
                type="text"
                class="datera-datepicker-start"
                placeholder="Select a date"
                v-validate="'required|required_from'"
                v-model="startDate"
                value=""
              />
              <svg
                name="calendar-icon"
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="20"
                viewBox="0 0 11.92 13.244"
                class="svg-icon mr-3"
              >
                <path
                  class="a"
                  d="M6.973,7.96H5.649V9.284H6.973Zm2.649,0H8.3V9.284H9.622Zm2.649,0H10.946V9.284h1.324ZM13.6,3.324h-.662V2H11.609V3.324h-5.3V2H4.987V3.324H4.324A1.318,1.318,0,0,0,3.007,4.649L3,13.92a1.324,1.324,0,0,0,1.324,1.324H13.6A1.328,1.328,0,0,0,14.92,13.92V4.649A1.328,1.328,0,0,0,13.6,3.324Zm0,10.6H4.324V6.635H13.6Z"
                  transform="translate(-3 -2)"
                  fill="inherit"
                />
              </svg>
              <span v-if="errors.has('startDate')" class="error">
                {{ errors.first('startDate') }}
              </span>
            </div>
          </div>
        </b-col>
        <b-col xs="12" md="6">
          <div class="input-container">
            <div class="drip-label font-weight-400">
              {{ $t('accountSummary.date.to') }}
            </div>
            <div class="spacing-8"></div>
            <div class="input-block">
              <input
                id="endDate"
                name="endDate"
                data-vv-as="To date"
                type="text"
                class="datera-datepicker-end"
                placeholder="Select a date"
                v-validate="'required|required_to'"
                v-model="endDate"
                value=""
              />
              <svg
                name="calendar-icon"
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="20"
                viewBox="0 0 11.92 13.244"
                class="svg-icon mr-3"
              >
                <path
                  class="a"
                  d="M6.973,7.96H5.649V9.284H6.973Zm2.649,0H8.3V9.284H9.622Zm2.649,0H10.946V9.284h1.324ZM13.6,3.324h-.662V2H11.609V3.324h-5.3V2H4.987V3.324H4.324A1.318,1.318,0,0,0,3.007,4.649L3,13.92a1.324,1.324,0,0,0,1.324,1.324H13.6A1.328,1.328,0,0,0,14.92,13.92V4.649A1.328,1.328,0,0,0,13.6,3.324Zm0,10.6H4.324V6.635H13.6Z"
                  transform="translate(-3 -2)"
                  fill="inherit"
                />
              </svg>
              <span v-if="errors.has('endDate')" class="error">
                {{ errors.first('endDate') }}
              </span>
            </div>
          </div>
        </b-col>
      </b-row>
      <div class="spacing-32"></div>

      <b-row class="mb-5">
        <b-col cols="12" md="6" xl="4">
          <action-button
            type="submit"
            variant="solid"
            extraClass="mr-3 d-flex align-items-center fullWidth"
          >
            <b-icon-arrow-repeat v-if="loading" class="mr-2" animation="spin" />
            {{ $t('accountSummary.date.download_button') }}
            <img
              v-if="!loading"
              src="@/assets/images/icons/download_icon.svg"
              alt="Download"
              height="16"
              class="ml-2"
            />
          </action-button>
        </b-col>
      </b-row>
    </form>
  </div>
</template>
<script>
import './datera.min.js'
import PageHeader from '@/components/common/PageHeader'
import ActionButton from '@/components/common/ActionButton'
import Multiselect from 'vue-multiselect'
import exportersService from '@/services/exporters'
import downloadService from '@/services/download'
import inputMasking from '@/helpers/input-masking'

export default {
  errorCaptured: function (err) {
    console.log('Caught error', err.message)
  },
  components: {
    PageHeader,
    ActionButton,
    Multiselect
  },
  data() {
    return {
      accounts: [],
      buyer: '',
      buyersData: [],
      buyerLoading: true,
      dateRangeSelected: '',
      startDate: '',
      endDate: '',
      isOpen: false,
      loading: false
    }
  },
  created() {
    this.$validator.extend('required_to', {
      getMessage: (field) => this.$t('accountSummary.date.validation'),
      validate: (value) => {
        if (this.startDate) {
          const [day1, month1, year1] = this.startDate.split('-')
          const [day2, month2, year2] = value.split('-')
          return this.compareDates(
            `${month1}-${day1}-${year1}`,
            `${month2}-${day2}-${year2}`
          )
        }
        return true
      }
    })
    this.$validator.extend('required_from', {
      getMessage: (field) => this.$t('accountSummary.date.validation'),
      validate: (value) => {
        if (this.endDate) {
          const [day1, month1, year1] = value.split('-')
          const [day2, month2, year2] = this.endDate.split('-')
          return this.compareDates(
            `${month1}-${day1}-${year1}`,
            `${month2}-${day2}-${year2}`
          )
        }
        return true
      }
    })
  },
  async mounted() {
    this.$mpTrack('Account summary page mounted')
    this.filterAccount()
    this.fetchAllBuyers()
    this.initDatepickers()
    document.addEventListener('datera-datepicker', this.resetValues)
    this.$mpTrack('Account Summary - page loaded')
  },
  beforeDestroy() {
    document.removeEventListener('datera-datepicker', this.resetValues)
  },
  watch: {
    dateRangeSelected(newVal) {
      this.setDatesOnCheckboxChange(newVal)
    }
  },
  methods: {
    compareDates(date1, date2) {
      if (new Date(date1) <= new Date(date2)) {
        return true
      }
      return false
    },
    momentFormat(val, format) {
      return this.$moment(val).format(format)
    },
    resetValues() {
      this.dateRangeSelected = ''
      this.startDate = this.momentFormat(
        window.DateraStart.value[0],
        'DD-MM-YYYY'
      )
      this.endDate = this.momentFormat(window.DateraEnd.value[0], 'DD-MM-YYYY')
    },
    goToDashboard() {
      this.$router.push({ name: 'dashboard' })
    },
    initDatepickers() {
      window.DateraStart = new window.Datera('datera-datepicker-start', {
        minYear: 2014,
        maxYear: new Date().getFullYear(),
        selectionType: 'single',
        theme: 'theme1-light',
        freezeDates: true
      })
      window.DateraEnd = new window.Datera('datera-datepicker-end', {
        minYear: 2014,
        maxYear: new Date().getFullYear(),
        selectionType: 'single',
        theme: 'theme2-light',
        freezeDates: true
      })
      window.DateraStart.mount()
      window.DateraEnd.mount()
    },
    downloadExcel() {
      this.loading = true
      this.$validator
        .validate()
        .then(async (result) => {
          if (result) {
            const startDate = this.momentFormat(
              window.DateraStart.value[0],
              'YYYY-MM-DD'
            )
            const endDate = this.momentFormat(
              window.DateraEnd.value[0],
              'YYYY-MM-DD'
            )

            let params = {
              start_date: startDate,
              end_date: endDate
            }
            if (this.buyer.id !== 1) {
              params.buyer_id = this.buyer.id
            }
            this.$mpTrack(
              'Account Summary - Download PDF of account summary',
              params
            )
            const res = await exportersService.downloadAccountSummaryExcel(
              this.$store.state.account.accountId,
              params
            )

            const filename = downloadService.createSummaryFilename(
              this.buyer.importer,
              this.startDate,
              this.endDate,
              this.dateRangeSelected
            )
            const flag = downloadService.downloadPDF(
              res.data,
              `${filename}.xlsx`
            )
            if (flag) {
              this.$mpTrack('Account Summary - Download successfully', params)
              this.$notify({
                title: 'Your file has been downloaded',
                type: 'success'
              })
            } else {
              this.$mpTrack('Account Summary - Download failed', params)
              this.$notify({
                title: 'Failed to download the summary',
                type: 'error'
              })
            }
          }
          this.loading = false
        })
        .catch((e) => {
          this.$mpTrack('Account Summary - failed to download file')
          this.loading = false
          console.log(e)
        })
    },
    getSubtractedDate(month, standardFormat) {
      if (standardFormat) {
        return this.$moment().subtract(month, 'months').format('DD-MM-YYYY')
      }
      return new Date(
        this.$moment().subtract(month, 'months').format('MM-DD-YYYY')
      )
    },
    getFinancialDateRange() {
      const year = this.$moment().year() - 1
      const startDate = this.$moment()
        .year(year)
        .startOf('year')
        .month(3)
        .startOf('month') // April 1st
      const endDate = this.$moment()
        .year(year + 1)
        .endOf('year')
        .month(2)
        .endOf('month') // March 31st

      return {
        startDate: startDate,
        endDate: endDate
      }
    },
    setDatesOnCheckboxChange(val) {
      let sDate, eDate

      if (val) this.$mpTrack(`Account Summary - Date range selected ${val}`)

      const dateTmp = this.getFinancialDateRange()

      switch (val) {
        case 'A':
          sDate = this.getSubtractedDate(1, false)
          eDate = this.getSubtractedDate(0, false)
          window.DateraStart.value = [sDate]
          window.DateraEnd.value = [eDate]
          this.startDate = this.getSubtractedDate(1, true)
          this.endDate = this.getSubtractedDate(0, true)
          window.DateraStart.currentDate.setMonth(sDate.getMonth())
          window.DateraStart.currentDate.setYear(sDate.getFullYear())
          window.DateraEnd.currentDate.setMonth(eDate.getMonth())
          break
        case 'B':
          sDate = this.getSubtractedDate(3, false)
          eDate = this.getSubtractedDate(0, false)
          window.DateraStart.value = [sDate]
          window.DateraEnd.value = [eDate]
          this.startDate = this.getSubtractedDate(3, true)
          this.endDate = this.getSubtractedDate(0, true)
          window.DateraStart.currentDate.setMonth(sDate.getMonth())
          window.DateraStart.currentDate.setYear(sDate.getFullYear())
          window.DateraEnd.currentDate.setMonth(eDate.getMonth())
          break
        case 'C':
          sDate = dateTmp.startDate
          eDate = dateTmp.endDate
          window.DateraStart.value = [sDate.toDate()]
          window.DateraEnd.value = [eDate.toDate()]
          this.startDate = sDate.format('DD-MM-YYYY')
          this.endDate = eDate.format('DD-MM-YYYY')
          window.DateraStart.currentDate.setMonth(sDate.toDate().getMonth())
          window.DateraStart.currentDate.setYear(sDate.toDate().getFullYear())
          window.DateraEnd.currentDate.setMonth(eDate.toDate().getMonth())
          break
      }
    },
    getLastThreeMonthLabel() {
      const prev3Month = this.$moment().subtract(3, 'months').format('MMMM')
      const currMonth = this.$moment().format('MMMM')
      const currYear = this.$moment().format('YYYY')
      return `(${prev3Month} ${currYear} - ${currMonth} ${currYear})`
    },
    getLastFinancialLabel() {
      const finStartMonth = 'April'
      const finEndMonth = 'March'
      const currYear = this.$moment().format('YYYY')
      const lastYear = this.$moment().subtract(1, 'years').format('YYYY')
      return `(${finStartMonth} ${lastYear} - ${finEndMonth} ${currYear})`
    },
    fetchAllBuyers() {
      exportersService
        .fetchExporterMetaData(this.$store.state.account.accountId, {
          include: ['clients_locked']
        })
        .then((res) => {
          this.$mpTrack(`Account Summary - Buyer dropdown loaded`)
          this.buyerLoading = false
          this.buyersData = [
            {
              id: 1,
              importer: 'All buyers'
            },
            ...res.data.clients_locked.map((x) => {
              return {
                id: x.importer_user_id,
                importer: x.importer
              }
            })
          ]
        })
    },
    filterAccount() {
      const account = this.$store.state.account
      if (account) {
        this.accounts.push({
          accountName: account.account.name,
          totalFacility:
            '$' + inputMasking.convertToCurrency(account.account.credit_limit),
          usedFacility:
            '$' + inputMasking.convertToCurrency(account.account.balance)
        })
      }
    }
  }
}
</script>
<style lang="scss">
#currency::placeholder {
  color: $neutral-gray-500;
}
.checkbox-container {
  div {
    display: flex;
    align-items: flex-start;
    @media screen and (max-width: 768px) {
      flex-direction: column;
    }
    div {
      margin-bottom: 2px;
      .custom-control-label {
        padding-top: 5px;
        padding-left: 2px;
      }
    }
  }
}
.divider-container {
  max-width: 750px;
  div {
    .divider {
      background: $sky-blue-500 !important;
      height: 2px;
    }
  }
}
.datera--calendar {
  z-index: 99;
  .datera--grid > .datera--day {
    padding: 0;
  }
}
</style>
<style lang="scss" scoped>
.account-summary-container {
  padding: 32px 108px;
  @media screen and (max-width: 1200px) {
    padding: 32px 56px;
  }
  @media screen and (max-width: 768px) {
    padding: 32px 24px;
  }
}
.input-container {
  width: 100%;
  margin-bottom: 16px;
  .input-label {
    font-size: 14px;
    font-weight: 400;
    line-height: 19px;
    letter-spacing: 0em;
    text-align: left;
    color: $noble-blue-500;
    margin-bottom: 8px;
  }
  .input-block {
    display: flex;
    flex-direction: column;
    position: relative;
    .svg-icon {
      position: absolute;
      right: 4px;
      top: 18px;
      fill: $wealthy-green-500;
    }
    .drip-dropdown {
      height: 56px;
      width: 500px;
      @media screen and (max-width: 768px) {
        width: 100%;
      }
      &.multiselect--disabled {
        ::v-deep .multiselect__tags {
          cursor: not-allowed;
          background: $neutral-gray-50;
        }
      }
      .action-input {
        position: absolute;
        cursor: pointer;
        right: 0;
        padding-left: 1rem;
        padding-right: 1rem;
        line-height: 2.6rem;
        font-size: 1.2rem;
        border-bottom-right-radius: 5px;
        border-top-right-radius: 5px;
        top: 1.6rem;
        &.inverse {
          transform: rotate(180deg);
        }
      }
      ::v-deep .multiselect__select {
        height: 100%;
        width: 20px;
        margin-right: 8px;
        padding: 0px;
        &::before {
          z-index: 1;
        }
      }
      ::v-deep .multiselect__tags {
        outline: 0px;
        height: 56px;
        padding: 16px;
        padding-right: 36px;
        border-radius: 8px;
        border: 1px solid $noble-blue-500;
        color: $noble-blue-500;
        margin-bottom: 4px;
        .multiselect__placeholder {
          color: $neutral-gray-500;
          margin-top: -2px;
          padding-top: 0px;
          padding-left: 5px;
          font-size: 16px;
        }
        .multiselect__single {
          clear: both;
          text-overflow: ellipsis;
          display: inline-block;
          overflow: hidden;
          white-space: nowrap;
          color: $noble-blue-500;
          font-weight: 700;
          vertical-align: bottom;
          width: 90% !important;
        }
      }
      ::v-deep .multiselect__option {
        background-color: white;
        color: $noble-blue-500;
      }
      ::v-deep .multiselect__option--highlight {
        background-color: white;
        color: $noble-blue-500;
      }
      ::v-deep .multiselect__option--selected {
        background-color: white;
      }
      ::v-deep .multiselect__option:hover {
        background-color: $sky-blue-50;
        color: $wealthy-green-600;
      }
      ::v-deep .multiselect__content-wrapper {
        border: 1px solid $noble-blue-50;
        box-shadow: 0px 6px 12px 0px #0a2e5733;
      }
    }
    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 30px white inset !important;
    }
    label {
      &.checkbox-label {
        margin-left: 12px;
        margin-top: 7px;
        margin-bottom: 0px;
      }
    }
    input {
      outline: 0px;
      max-width: 400px;
      height: 56px;
      padding: 16px;
      border-radius: 8px;
      border: 1px solid $noble-blue-500;
      color: $noble-blue-500;
      font-size: 16px;
      font-weight: 700;

      &::placeholder {
        font-weight: 400;
        color: $neutral-gray-500;
      }
      &.width-800 {
        width: 808px;
      }
    }
    .error {
      color: $red-400;
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      letter-spacing: 0em;
      text-align: left;
      margin-top: 8px;
      @media screen and (max-width: 768px) {
        margin-top: 2px;
      }
    }
  }
  .supporting-document {
    display: flex;
    flex-direction: column;
    .error {
      color: $red-400;
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      letter-spacing: 0em;
      text-align: left;
      margin-top: 8px;
      @media screen and (max-width: 768px) {
        margin-top: 2px;
      }
    }
  }
}
</style>
